import { useEffect, useMemo, useState } from 'react';
import { propertyTypes, stateLocations } from '../propertyConfig';
import { getPropertyArea, getPropertyState, isTruthy } from '../propertyUtils';
import { trackEvent } from '../analyticsLoader';

const LISTING_TYPE_LABELS = { rent: 'Rent', buy: 'For Sale', 'short-let': 'Short-let' };
const PROPERTY_TYPE_LABELS = Object.fromEntries(
  propertyTypes.map(([value, label]) => [value, label]),
);

// Free-text filter match: true if the search text is a substring of either the
// raw stored value or its friendly label (e.g. typing "plot" matches propertyType
// "Land", whose label is "Land / Plot"), so users aren't limited to exact picks.
function matchesFreeText(rawValue, labelMap, searchText) {
  if (!searchText) {return true;}
  const search = searchText.trim().toLowerCase();
  if (!search) {return true;}
  const value = String(rawValue || '');
  const label = labelMap[value] || value;
  return value.toLowerCase().includes(search) || label.toLowerCase().includes(search);
}

// Filters that apply to any property list regardless of where it was fetched
// from (the generic properties endpoint or the dedicated short-lets
// endpoint) - amenity flags, min bathrooms, and posted-within window aren't
// supported server-side by the short-lets endpoint, so BrowsePage runs this
// over its server-fetched short-let results too, keeping behavior identical
// to the generic browsing path instead of silently dropping these filters.
export function applyClientRefinements(
  properties,
  { filterFlags, minBathrooms, postedWithin, filterAmenities = [], filterPropertyDocuments = [] },
) {
  const now = Date.now();
  const postedWindowMs = {
    any: null,
    today: 24 * 60 * 60 * 1000,
    week: 7 * 24 * 60 * 60 * 1000,
    month: 30 * 24 * 60 * 60 * 1000,
  };

  return properties
    .filter((item) => {
      if (!filterAmenities || filterAmenities.length === 0) {
        return true;
      }
      let itemAmenities = item.amenities;
      if (typeof itemAmenities === 'string') {
        try {
          itemAmenities = JSON.parse(itemAmenities);
        } catch {
          itemAmenities = [];
        }
      }
      if (!Array.isArray(itemAmenities)) {
        return false;
      }
      return filterAmenities.every((key) => itemAmenities.includes(key));
    })
    .filter((item) => {
      if (!filterPropertyDocuments || filterPropertyDocuments.length === 0) {
        return true;
      }
      let itemDocs = item.property_documents ?? item.propertyDocuments;
      if (typeof itemDocs === 'string') {
        try {
          itemDocs = JSON.parse(itemDocs);
        } catch {
          itemDocs = [];
        }
      }
      if (!Array.isArray(itemDocs)) {
        return false;
      }
      return filterPropertyDocuments.every((key) => itemDocs.includes(key));
    })
    .filter((item) => !minBathrooms || Number(item.bathrooms) >= Number(minBathrooms))
    .filter((item) => !filterFlags.filterFurnished || isTruthy(item.furnished))
    .filter((item) => !filterFlags.filterServiced || isTruthy(item.serviced))
    .filter((item) => !filterFlags.filterSelfContained || isTruthy(item.selfContained))
    .filter((item) => !filterFlags.filterNewDevelopment || isTruthy(item.newDevelopment))
    .filter((item) => !filterFlags.filterParking || isTruthy(item.parking))
    .filter((item) => !filterFlags.filterGated || isTruthy(item.gated))
    .filter((item) => !filterFlags.filterBoysQuarters || isTruthy(item.boysQuarters))
    .filter((item) => !filterFlags.filterPrepaidMeter || isTruthy(item.prepaidMeter))
    .filter((item) => !filterFlags.filterBorehole || isTruthy(item.borehole))
    .filter((item) => !filterFlags.filterStudentFriendly || isTruthy(item.studentFriendly))
    .filter((item) => !filterFlags.filterVerified || isTruthy(item.isVerified))
    .filter((item) => !filterFlags.filterNoAgencyFee || isTruthy(item.noAgencyFee))
    .filter((item) => !filterFlags.filterNearSchool || isTruthy(item.nearSchool))
    .filter((item) => !filterFlags.filterNearMarket || isTruthy(item.nearMarket))
    .filter((item) => !filterFlags.filterNearExpressway || isTruthy(item.nearExpressway))
    .filter((item) => !filterFlags.filterFloodFree || isTruthy(item.floodFree))
    .filter((item) => {
      if (postedWithin === 'any') {
        return true;
      }
      const postedAt = new Date(item.createdAt).getTime();
      if (!postedAt) {
        return false;
      }
      return now - postedAt <= postedWindowMs[postedWithin];
    });
}

const defaultFilterFlags = {
  filterFurnished: false,
  filterServiced: false,
  filterSelfContained: false,
  filterNewDevelopment: false,
  filterParking: false,
  filterGated: false,
  filterBoysQuarters: false,
  filterPrepaidMeter: false,
  filterBorehole: false,
  filterStudentFriendly: false,
  filterVerified: false,
  filterNoAgencyFee: false,
  filterNearSchool: false,
  filterNearMarket: false,
  filterNearExpressway: false,
  filterFloodFree: false,
};

export function usePropertyFilters(properties) {
  const [filterLocation, setFilterLocation] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPropertyType, setFilterPropertyType] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [minBedrooms, setMinBedrooms] = useState('');
  const [minBathrooms, setMinBathrooms] = useState('');
  const [postedWithin, setPostedWithin] = useState('any');
  const [sortBy, setSortBy] = useState('newest');
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [filterFlags, setFilterFlags] = useState(defaultFilterFlags);
  const [filterAmenities, setFilterAmenities] = useState([]);
  const [filterPropertyDocuments, setFilterPropertyDocuments] = useState([]);

  const toggleFilterAmenity = (key) => {
    setFilterAmenities((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  };

  const toggleFilterPropertyDocument = (key) => {
    setFilterPropertyDocuments((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );
  };

  // GA4 conversion-funnel event, debounced so a user typing a state name
  // doesn't fire one event per keystroke. Only fires once at least one
  // criterion is actually set, not on the initial empty/default state.
  useEffect(() => {
    const hasCriteria =
      filterLocation || filterArea || filterType || filterPropertyType || minPrice || maxPrice || minBedrooms || minBathrooms;
    if (!hasCriteria) {
      return undefined;
    }
    const timer = setTimeout(() => {
      trackEvent('search_performed', {
        state: filterLocation || undefined,
        area: filterArea || undefined,
        listing_type: filterType || undefined,
        property_type: filterPropertyType || undefined,
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [filterLocation, filterArea, filterType, filterPropertyType, minPrice, maxPrice, minBedrooms, minBathrooms]);

  const categoryCounts = useMemo(
    () => ({
      Apartment: properties.filter((item) => (item.propertyType || 'Apartment') === 'Apartment')
        .length,
      House: properties.filter((item) => (item.propertyType || 'Apartment') === 'House').length,
      Duplex: properties.filter((item) => (item.propertyType || 'Apartment') === 'Duplex').length,
      Bungalow: properties.filter((item) => (item.propertyType || 'Apartment') === 'Bungalow')
        .length,
      Flat: properties.filter((item) => (item.propertyType || 'Apartment') === 'Flat').length,
      Room: properties.filter((item) => (item.propertyType || 'Apartment') === 'Room').length,
      Terrace: properties.filter((item) => (item.propertyType || 'Apartment') === 'Terrace').length,
      Penthouse: properties.filter((item) => (item.propertyType || 'Apartment') === 'Penthouse')
        .length,
      Land: properties.filter((item) => (item.propertyType || 'Apartment') === 'Land').length,
      Commercial: properties.filter((item) => (item.propertyType || 'Apartment') === 'Commercial')
        .length,
      Garage: properties.filter((item) => (item.propertyType || 'Apartment') === 'Garage').length,
      serviced: properties.filter((item) => isTruthy(item.serviced)).length,
    }),
    [properties],
  );

  const filteredProperties = useMemo(() => {
    const locationFiltered = properties
      .filter(
        (item) =>
          !filterLocation ||
          String(getPropertyState(item) || '')
            .toLowerCase()
            .includes(filterLocation.trim().toLowerCase()),
      )
      .filter(
        (item) =>
          !filterArea || String(getPropertyArea(item)).toLowerCase() === filterArea.toLowerCase(),
      )
      .filter((item) =>
        matchesFreeText(item.type || item.listing_type, LISTING_TYPE_LABELS, filterType),
      )
      .filter((item) =>
        matchesFreeText(item.propertyType || 'Apartment', PROPERTY_TYPE_LABELS, filterPropertyType),
      )
      .filter((item) => !minPrice || Number(item.price) >= Number(minPrice))
      .filter((item) => !maxPrice || Number(item.price) <= Number(maxPrice))
      .filter((item) => !minBedrooms || Number(item.bedrooms) >= Number(minBedrooms));

    return applyClientRefinements(locationFiltered, {
      filterFlags,
      minBathrooms,
      postedWithin,
      filterAmenities,
      filterPropertyDocuments,
    })
      .sort((a, b) => {
        if (sortBy === 'price-low') {
          return Number(a.price) - Number(b.price);
        }
        if (sortBy === 'price-high') {
          return Number(b.price) - Number(a.price);
        }
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
  }, [
    filterAmenities,
    filterPropertyDocuments,
    filterArea,
    filterFlags,
    filterLocation,
    filterPropertyType,
    filterType,
    maxPrice,
    minBathrooms,
    minBedrooms,
    minPrice,
    postedWithin,
    properties,
    sortBy,
  ]);

  const browseAreas = filterLocation ? stateLocations[filterLocation] || [] : [];

  // Spotlight rotation: always show exactly 3 slots. Featured (referral-reward)
  // properties fill first, backfilled with non-featured when the featured pool
  // is under 3. At 4+ featured properties, a sliding window of 3 rotates across
  // a stable, id-sorted circular pool every 30 minutes so no item is starved and
  // each item's share degrades predictably (3/N) as more items compete.
  const featuredListings = useMemo(() => {
    const featuredPool = filteredProperties
      .filter((item) => item.isFeatured)
      .slice()
      .sort((a, b) => Number(a.id) - Number(b.id));

    const n = featuredPool.length;
    if (n === 0) {
      return filteredProperties.slice(0, 3);
    }
    if (n < 4) {
      const nonFeatured = filteredProperties.filter((item) => !item.isFeatured);
      return [...featuredPool, ...nonFeatured].slice(0, 3);
    }

    const ROTATION_BUCKET_MS = 30 * 60 * 1000;
    const bucket = Math.floor(Date.now() / ROTATION_BUCKET_MS);
    const offset = bucket % n;
    return [0, 1, 2].map((i) => featuredPool[(offset + i) % n]);
  }, [filteredProperties]);

  const resetAllFilters = () => {
    setFilterLocation('');
    setFilterArea('');
    setFilterType('');
    setFilterPropertyType('');
    setMinPrice('');
    setMaxPrice('');
    setMinBedrooms('');
    setMinBathrooms('');
    setPostedWithin('any');
    setSortBy('newest');
    setFilterFlags(defaultFilterFlags);
    setFilterAmenities([]);
    setFilterPropertyDocuments([]);
  };

  const filterSetters = Object.fromEntries(
    Object.keys(defaultFilterFlags).map((key) => [
      key,
      (value) => setFilterFlags((current) => ({ ...current, [key]: value })),
    ]),
  );

  return {
    filterLocation,
    setFilterLocation,
    filterArea,
    setFilterArea,
    filterType,
    setFilterType,
    filterPropertyType,
    setFilterPropertyType,
    minPrice,
    setMinPrice,
    maxPrice,
    setMaxPrice,
    minBedrooms,
    setMinBedrooms,
    minBathrooms,
    setMinBathrooms,
    postedWithin,
    setPostedWithin,
    sortBy,
    setSortBy,
    isFilterDrawerOpen,
    setIsFilterDrawerOpen,
    filterFlags,
    filterSetters,
    filterAmenities,
    toggleFilterAmenity,
    filterPropertyDocuments,
    toggleFilterPropertyDocument,
    categoryCounts,
    filteredProperties,
    featuredListings,
    browseAreas,
    resetAllFilters,
  };
}
